import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import { ja } from "date-fns/locale";
import {
  Activity,
  ArrowRight,
  Clock3,
  KeyRound,
  LayoutTemplate,
  RefreshCcw,
  Radio,
  Rocket,
  Settings2,
  Wallet,
  Webhook,
} from "lucide-react";
import { toast } from "react-hot-toast";
import { Link } from "react-router-dom";

import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { StatsCard } from "@/components/dashboard/StatsCard";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { useDashboard } from "@/hooks/useDashboard";
import { useAuth } from "@/hooks/useAuth";
import { api } from "@/services/api";
import { SyncResult, TrackedAccount } from "@/types";

const statusLabels: Record<TrackedAccount["last_sync_status"], string> = {
  idle: "待機",
  running: "実行中",
  success: "成功",
  failed: "失敗",
  blocked: "停止中",
};

const statusClasses: Record<TrackedAccount["last_sync_status"], string> = {
  idle: "bg-slate-100 text-slate-700",
  running: "bg-blue-100 text-blue-700",
  success: "bg-emerald-100 text-emerald-700",
  failed: "bg-rose-100 text-rose-700",
  blocked: "bg-amber-100 text-amber-700",
};

const formatRelativeTime = (dateString?: string) => {
  if (!dateString) return "未実行";
  try {
    return formatDistanceToNow(new Date(dateString), {
      addSuffix: true,
      locale: ja,
    });
  } catch {
    return "不明";
  }
};

const safeNumber = (value: unknown) => {
  const normalized = Number(value);
  return Number.isFinite(normalized) ? normalized : 0;
};

const quickLinks = [
  {
    title: "X API設定",
    description: "Bearer Token の登録と credential の切替",
    href: "/dashboard/settings?tab=x-api",
    icon: Settings2,
    tone: "bg-blue-50 text-blue-700 border-blue-200",
  },
  {
    title: "Tracked Accounts",
    description: "取得対象、間隔、日次・月次 cap を管理",
    href: "/dashboard/settings?tab=accounts",
    icon: Radio,
    tone: "bg-emerald-50 text-emerald-700 border-emerald-200",
  },
  {
    title: "Widget設定",
    description: "テーマ、表示件数、埋め込みコード",
    href: "/dashboard/settings?tab=widget",
    icon: LayoutTemplate,
    tone: "bg-violet-50 text-violet-700 border-violet-200",
  },
  {
    title: "APIキー管理",
    description: "公開用 key の発行、停止、棚卸し",
    href: "/dashboard/api-keys",
    icon: KeyRound,
    tone: "bg-amber-50 text-amber-700 border-amber-200",
  },
  {
    title: "取得履歴",
    description: "API取得ログと最新取得投稿の一覧",
    href: "/dashboard/activity",
    icon: Activity,
    tone: "bg-slate-100 text-slate-700 border-slate-200",
  },
] as const;

export const DashboardPage = () => {
  const queryClient = useQueryClient();
  const { tenant } = useAuth();
  const { usage, trackedAccounts, recentTweets, isLoading, error } = useDashboard();
  const [syncingId, setSyncingId] = useState<string | null>(null);
  const trackedAccountsTotalPostsFetched = trackedAccounts.reduce(
    (sum, account) => sum + safeNumber(account.total_posts_fetched),
    0
  );
  const trackedAccountsTotalEstimatedCost = trackedAccounts.reduce(
    (sum, account) => sum + safeNumber(account.estimated_total_cost_usd),
    0
  );

  const syncMutation = useMutation({
    mutationFn: async (accountId: string) => {
      const result = await api.post<SyncResult>(`/tracked-accounts/${accountId}/sync`);
      return { accountId, result };
    },
    onMutate: (accountId: string) => {
      setSyncingId(accountId);
    },
    onSuccess: ({ result }) => {
      toast.success(result.message || "手動同期を開始しました");
      queryClient.invalidateQueries({ queryKey: ["dashboard-data"] });
    },
    onError: (mutationError: unknown) => {
      console.error("Sync error:", mutationError);
      toast.error("同期の開始に失敗しました");
    },
    onSettled: () => {
      setSyncingId(null);
    },
  });

  if (error) {
    return (
      <DashboardLayout>
        <div className="py-12 text-center">
          <Activity className="mx-auto mb-4 h-12 w-12 text-red-500" />
          <h3 className="mb-2 text-lg font-medium text-gray-900">
            ダッシュボードの読み込みに失敗しました
          </h3>
          <p className="mb-4 text-gray-600">
            API または利用量集計の取得に失敗しています。
          </p>
          <Button onClick={() => window.location.reload()}>再読み込み</Button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-[1.2fr,0.8fr]">
          <div className="rounded-3xl border border-slate-200 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 p-6 text-white shadow-sm">
            <div className="flex items-start justify-between gap-4">
              <div className="space-y-3">
                <p className="text-xs font-medium uppercase tracking-[0.24em] text-slate-400">
                  Tenant Operations
                </p>
                <div>
                  <h1 className="text-3xl font-semibold tracking-tight">運用ダッシュボード</h1>
                  <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-300">
                    X API の利用量、対象アカウント、保存済み投稿、公開 widget の状態を
                    ここで確認します。設定変更は右側のショートカットから直接移動できます。
                  </p>
                </div>
                <div className="flex flex-wrap gap-3">
                  <div className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3">
                    <p className="text-xs text-slate-400">tenant</p>
                    <p className="mt-1 text-sm font-medium text-white">
                      {tenant?.name || "未取得"}
                    </p>
                  </div>
                  <div className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3">
                    <p className="text-xs text-slate-400">plan</p>
                    <p className="mt-1 text-sm font-medium text-white">
                      {tenant?.plan_id || "basic"}
                    </p>
                  </div>
                  <div className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3">
                    <p className="text-xs text-slate-400">public api key</p>
                    <p className="mt-1 text-sm font-medium text-white">
                      {tenant?.api_key ? "発行済み" : "未発行"}
                    </p>
                  </div>
                </div>
              </div>
              <Button
                variant="ghost"
                className="border border-white/10 text-white hover:bg-white/10 hover:text-white"
                onClick={() =>
                  queryClient.invalidateQueries({ queryKey: ["dashboard-data"] })
                }
              >
                <RefreshCcw className="mr-2 h-4 w-4" />
                更新
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {quickLinks.map((link) => {
              const Icon = link.icon;
              return (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`group rounded-2xl border p-4 transition-all hover:-translate-y-0.5 hover:shadow-sm ${link.tone}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="space-y-2">
                      <Icon className="h-5 w-5" />
                      <div>
                        <p className="font-medium">{link.title}</p>
                        <p className="text-sm leading-5 opacity-80">{link.description}</p>
                      </div>
                    </div>
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>

        {isLoading ? (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
            {[...Array(4)].map((_, index) => (
              <div key={index} className="animate-pulse rounded-lg border bg-white p-6">
                <div className="mb-4 h-4 w-24 rounded bg-gray-200" />
                <div className="h-8 w-20 rounded bg-gray-200" />
              </div>
            ))}
          </div>
        ) : usage ? (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
            <StatsCard
              title="追跡アカウント"
              value={`${usage.active_tracked_accounts}/${usage.total_tracked_accounts}`}
              icon={Radio}
              color="blue"
            />
            <StatsCard
              title="Widget公開中"
              value={usage.widget_enabled_accounts}
              icon={Webhook}
              color="green"
            />
            <StatsCard
              title="今月の取得投稿数"
              value={`${safeNumber(usage.monthly_posts_used).toLocaleString()} / ${safeNumber(usage.monthly_posts_limit).toLocaleString()}`}
              icon={Rocket}
              color="yellow"
            />
            <StatsCard
              title="本日のAPI取得投稿数"
              value={safeNumber(usage.posts_fetched_today).toLocaleString()}
              icon={Activity}
              color="blue"
            />
            <StatsCard
              title="今月の見込みコスト"
              value={`$${safeNumber(usage.estimated_monthly_cost_usd).toFixed(2)}`}
              icon={Wallet}
              color="red"
            />
            <StatsCard
              title="累計取得投稿数"
              value={safeNumber(usage.total_posts_fetched).toLocaleString()}
              icon={Rocket}
              color="purple"
            />
            <StatsCard
              title="累計見込みコスト"
              value={`$${safeNumber(usage.estimated_total_cost_usd).toFixed(2)}`}
              icon={Wallet}
              color="red"
            />
          </div>
        ) : null}

        <div className="grid grid-cols-1 gap-6 xl:grid-cols-[0.9fr,1.1fr,1fr]">
          <Card>
            <CardHeader>
              <h3 className="text-lg font-medium text-gray-900">同期状況</h3>
            </CardHeader>
            <CardBody className="space-y-4">
              <div className="flex items-center justify-between rounded-lg bg-slate-50 px-4 py-3">
                <div>
                  <p className="text-sm text-slate-600">本日のAPI呼び出し</p>
                  <p className="text-xl font-semibold text-slate-900">
                    {safeNumber(usage?.api_calls_today).toLocaleString()}
                  </p>
                </div>
                <Activity className="h-8 w-8 text-slate-400" />
              </div>
              <div className="flex items-center justify-between rounded-lg bg-slate-50 px-4 py-3">
                <div>
                  <p className="text-sm text-slate-600">本日同期済み</p>
                  <p className="text-xl font-semibold text-slate-900">
                    {safeNumber(usage?.synced_today).toLocaleString()}
                  </p>
                </div>
                <RefreshCcw className="h-8 w-8 text-slate-400" />
              </div>
              <div className="flex items-center justify-between rounded-lg bg-slate-50 px-4 py-3">
                <div>
                  <p className="text-sm text-slate-600">本日のAPI取得投稿数</p>
                  <p className="text-xl font-semibold text-slate-900">
                    {safeNumber(usage?.posts_fetched_today).toLocaleString()}
                  </p>
                </div>
                <Rocket className="h-8 w-8 text-slate-400" />
              </div>
              <div className="flex items-center justify-between rounded-lg bg-slate-50 px-4 py-3">
                <div>
                  <p className="text-sm text-slate-600">最終同期</p>
                  <p className="text-sm font-medium text-slate-900">
                    {formatRelativeTime(usage?.last_sync_at)}
                  </p>
                </div>
                <Clock3 className="h-8 w-8 text-slate-400" />
              </div>
              <div className="rounded-lg bg-slate-50 px-4 py-3">
                <p className="text-sm text-slate-600">累計メトリクス</p>
                <div className="mt-2 flex items-center justify-between gap-4 text-sm">
                  <span className="text-slate-500">取得投稿数</span>
                  <span className="font-semibold text-slate-900">
                    {safeNumber(usage?.total_posts_fetched).toLocaleString()}
                  </span>
                </div>
                <div className="mt-2 flex items-center justify-between gap-4 text-sm">
                  <span className="text-slate-500">見込みコスト</span>
                  <span className="font-semibold text-slate-900">
                    ${safeNumber(usage?.estimated_total_cost_usd).toFixed(2)}
                  </span>
                </div>
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardHeader>
              <h3 className="text-lg font-medium text-gray-900">設定チェック</h3>
            </CardHeader>
            <CardBody className="space-y-3">
              <Link
                to="/dashboard/settings?tab=x-api"
                className="flex items-center justify-between rounded-xl border border-gray-200 px-4 py-3 text-sm transition-colors hover:border-primary-200 hover:bg-primary-50"
              >
                <div>
                  <p className="font-medium text-gray-900">X API credential</p>
                  <p className="text-gray-500">
                    {trackedAccounts.length > 0
                      ? "同期前に token の有効性を確認"
                      : "まず token を登録"}
                  </p>
                </div>
                <ArrowRight className="h-4 w-4 text-gray-400" />
              </Link>
              <Link
                to="/dashboard/settings?tab=accounts"
                className="flex items-center justify-between rounded-xl border border-gray-200 px-4 py-3 text-sm transition-colors hover:border-primary-200 hover:bg-primary-50"
              >
                <div>
                  <p className="font-medium text-gray-900">Tracked Accounts</p>
                  <p className="text-gray-500">
                    {trackedAccounts.length === 0
                      ? "取得対象を追加"
                      : `${trackedAccounts.length} 件を管理中`}
                  </p>
                </div>
                <ArrowRight className="h-4 w-4 text-gray-400" />
              </Link>
              <Link
                to="/dashboard/settings?tab=widget"
                className="flex items-center justify-between rounded-xl border border-gray-200 px-4 py-3 text-sm transition-colors hover:border-primary-200 hover:bg-primary-50"
              >
                <div>
                  <p className="font-medium text-gray-900">Widget</p>
                  <p className="text-gray-500">
                    埋め込みコードと公開設定を確認
                  </p>
                </div>
                <ArrowRight className="h-4 w-4 text-gray-400" />
              </Link>
              <Link
                to="/dashboard/api-keys"
                className="flex items-center justify-between rounded-xl border border-gray-200 px-4 py-3 text-sm transition-colors hover:border-primary-200 hover:bg-primary-50"
              >
                <div>
                  <p className="font-medium text-gray-900">APIキー管理</p>
                  <p className="text-gray-500">
                    {tenant?.api_key ? "現在の公開 key を確認" : "公開 key を発行"}
                  </p>
                </div>
                <ArrowRight className="h-4 w-4 text-gray-400" />
              </Link>
            </CardBody>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-center justify-between gap-3">
                <h3 className="text-lg font-medium text-gray-900">Tracked Accounts</h3>
                <span className="text-sm text-gray-500">{trackedAccounts.length} 件</span>
              </div>
            </CardHeader>
            <CardBody>
              {trackedAccounts.length === 0 ? (
                <p className="text-sm text-gray-600">
                  まだ対象アカウントが登録されていません。設定画面から追加してください。
                </p>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div className="rounded-xl border border-gray-200 bg-slate-50 px-4 py-3">
                      <p className="text-xs uppercase tracking-wide text-gray-400">
                        全アカウント累計取得投稿数
                      </p>
                      <p className="mt-1 text-lg font-semibold text-gray-900">
                        {trackedAccountsTotalPostsFetched.toLocaleString()}
                      </p>
                    </div>
                    <div className="rounded-xl border border-gray-200 bg-slate-50 px-4 py-3">
                      <p className="text-xs uppercase tracking-wide text-gray-400">
                        全アカウント累計見込みコスト
                      </p>
                      <p className="mt-1 text-lg font-semibold text-gray-900">
                        ${trackedAccountsTotalEstimatedCost.toFixed(2)}
                      </p>
                    </div>
                  </div>
                  {trackedAccounts.slice(0, 3).map((account) => (
                    <div
                      key={account.id}
                      className="rounded-xl border border-gray-200 p-4 transition-colors hover:border-primary-200"
                    >
                      {(() => {
                        const totalPostsFetched = safeNumber(account.total_posts_fetched);
                        const estimatedTotalCostUsd = safeNumber(account.estimated_total_cost_usd);

                        return (
                      <div className="flex flex-col gap-4">
                        <div className="space-y-3">
                          <div className="flex flex-wrap items-center gap-3">
                            <div>
                              <p className="text-base font-semibold text-gray-900">
                                @{account.screen_name}
                              </p>
                              <p className="text-sm text-gray-500">
                                {account.display_name || "表示名未設定"}
                              </p>
                            </div>
                            <span
                              className={`rounded-full px-2.5 py-1 text-xs font-medium ${statusClasses[account.last_sync_status]}`}
                            >
                              {statusLabels[account.last_sync_status]}
                            </span>
                            {account.widget_enabled ? (
                              <span className="rounded-full bg-purple-100 px-2.5 py-1 text-xs font-medium text-purple-700">
                                Widget公開
                              </span>
                            ) : null}
                          </div>

                          <div className="grid grid-cols-2 gap-3 text-sm text-gray-600 md:grid-cols-4">
                            <div>
                              <p className="text-xs uppercase tracking-wide text-gray-400">
                                保存投稿
                              </p>
                              <p className="font-medium text-gray-900">
                                {safeNumber(account.posts_stored).toLocaleString()}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs uppercase tracking-wide text-gray-400">
                                同期間隔
                              </p>
                              <p className="font-medium text-gray-900">
                                {safeNumber(account.sync_interval_minutes).toLocaleString()}分
                              </p>
                            </div>
                            <div>
                              <p className="text-xs uppercase tracking-wide text-gray-400">
                                1回上限
                              </p>
                              <p className="font-medium text-gray-900">
                                {safeNumber(account.max_posts_per_sync).toLocaleString()}件
                              </p>
                            </div>
                            <div>
                              <p className="text-xs uppercase tracking-wide text-gray-400">
                                最終同期
                              </p>
                              <p className="font-medium text-gray-900">
                                {formatRelativeTime(account.last_synced_at)}
                              </p>
                            </div>
                          </div>
                          <div className="grid grid-cols-1 gap-3 text-sm text-gray-600 md:grid-cols-2">
                            <div className="rounded-lg bg-slate-50 px-3 py-2">
                              <p className="text-xs uppercase tracking-wide text-gray-400">
                                アカウント累計取得投稿数
                              </p>
                              <p className="font-medium text-gray-900">
                                {totalPostsFetched.toLocaleString()}
                              </p>
                            </div>
                            <div className="rounded-lg bg-slate-50 px-3 py-2">
                              <p className="text-xs uppercase tracking-wide text-gray-400">
                                アカウント累計見込みコスト
                              </p>
                              <p className="font-medium text-gray-900">
                                ${estimatedTotalCostUsd.toFixed(2)}
                              </p>
                            </div>
                          </div>

                          {account.last_sync_error ? (
                            <p className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">
                              {account.last_sync_error}
                            </p>
                          ) : null}
                        </div>

                        <div className="flex shrink-0 items-center gap-2">
                          <Button
                            variant="ghost"
                            disabled={syncingId === account.id || !account.is_active}
                            onClick={() => syncMutation.mutate(account.id)}
                          >
                            <RefreshCcw className="mr-2 h-4 w-4" />
                            {syncingId === account.id ? "同期中..." : "手動同期"}
                          </Button>
                        </div>
                      </div>
                        );
                      })()}
                    </div>
                  ))}
                  {trackedAccounts.length > 3 ? (
                    <Link
                      to="/dashboard/settings?tab=accounts"
                      className="inline-flex items-center text-sm font-medium text-primary-700"
                    >
                      すべての tracked accounts を見る
                      <ArrowRight className="ml-1 h-4 w-4" />
                    </Link>
                  ) : null}
                </div>
              )}
            </CardBody>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between gap-3">
              <div>
                <h3 className="text-lg font-medium text-gray-900">取得履歴</h3>
                <p className="text-sm text-gray-500">
                  一覧性の高い情報は専用ページへ移しました。
                </p>
              </div>
              <Link
                to="/dashboard/activity"
                className="inline-flex items-center text-sm font-medium text-primary-700"
              >
                取得履歴を開く
                <ArrowRight className="ml-1 h-4 w-4" />
              </Link>
            </div>
          </CardHeader>
          <CardBody className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="rounded-xl border border-gray-200 bg-slate-50 px-4 py-3">
              <p className="text-xs uppercase tracking-wide text-gray-400">
                最近のAPI取得ログ
              </p>
              <p className="mt-1 text-lg font-semibold text-gray-900">
                {(usage?.recent_api_logs?.length ?? 0).toLocaleString()} 件
              </p>
              <p className="mt-2 text-sm text-gray-500">
                API取得件数、実行結果、コスト計算を一覧で確認できます。
              </p>
            </div>
            <div className="rounded-xl border border-gray-200 bg-slate-50 px-4 py-3">
              <p className="text-xs uppercase tracking-wide text-gray-400">
                最新取得投稿
              </p>
              <p className="mt-1 text-lg font-semibold text-gray-900">
                {recentTweets.length.toLocaleString()} 件
              </p>
              <p className="mt-2 text-sm text-gray-500">
                保存済みの最新投稿をまとめて確認できます。
              </p>
            </div>
          </CardBody>
        </Card>
      </div>
    </DashboardLayout>
  );
};
